// Registering the service worker, and the one thing it needs the page for.
//
// The worker itself is generated — see frontend/pwa.ts. This is the half that
// has to live in the bundle, because only the page can ask the reader whether
// now is a good moment to reload.
//
// ── Why there is a prompt at all ───────────────────────────────────────────
//
// A service worker that calls skipWaiting() on install replaces the running
// code under whoever is using it. On most sites that is a shrug. Here the
// reader is in the middle of something with state the URL does not hold — a
// popup open on a bus stop, a layer just switched on, a search half typed —
// and a silent reload throws it away at a moment they did not choose.
//
// The alternative default, letting the new worker wait until every tab is
// closed, is honest but slow: a map people keep open on a phone can sit on a
// release for days, which is the "cached shell outlives the deploy" problem
// #57 raised.
//
// So: the worker waits, and the page offers. One line, dismissible, and the
// reader decides. The documents themselves are network-first inside the
// worker, so this is about the JavaScript, not about serving a stale page.

import { track } from './analytics.ts';

/** Where the generated worker lands. Must match SW_PATH in frontend/pwa.ts. */
const SW_URL = '/sw.js';

/**
 * Offer the reader a reload, once.
 *
 * Built in JavaScript rather than sitting hidden in index.html because it is
 * the rarest thing on the page — most sessions never see it — and markup that
 * ships to everyone to be displayed to almost nobody is markup that rots
 * unnoticed. Styled from the same variables as the panels so it cannot drift
 * from them.
 */
function offerReload(worker: ServiceWorker): void {
  if (document.getElementById('sw-update')) return;

  const bar = document.createElement('div');
  bar.id = 'sw-update';
  bar.setAttribute('role', 'status');
  bar.innerHTML = `
    <span>A new version is ready.</span>
    <button type="button" id="sw-update-go">Reload</button>
    <button type="button" id="sw-update-no" aria-label="Dismiss">×</button>`;
  document.body.append(bar);

  bar.querySelector('#sw-update-go')!.addEventListener('click', () => {
    track('pwa-update:accept');
    // The worker activates, controllerchange fires below, and that is what
    // reloads. Doing it here instead would race: the page could come back
    // before the new worker had taken over and be served the old shell.
    worker.postMessage('skip-waiting');
  });
  bar.querySelector('#sw-update-no')!.addEventListener('click', () => {
    track('pwa-update:dismiss');
    bar.remove();
  });
}

/**
 * Register the worker, and watch for a replacement.
 *
 * Everything here is best-effort. A browser with service workers disabled, a
 * private window that refuses the registration, an insecure origin — all of
 * them land in a catch and leave a working map that simply is not installable.
 * A map that fails to load because its offline support failed to register
 * would be a poor trade.
 */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  // Not in dev. The Vite dev server has no built assets to precache and no
  // hashed names to make cache-first safe, so a worker there would serve
  // yesterday's module over today's edit — a debugging experience this
  // project does not need to invent for itself.
  if (import.meta.env.DEV) return;

  window.addEventListener('load', () => {
    void (async () => {
      try {
        const reg = await navigator.serviceWorker.register(SW_URL, { scope: '/' });

        // A worker already waiting when the page loaded: the reader has been
        // here since a deploy and has not reloaded yet.
        if (reg.waiting && navigator.serviceWorker.controller) offerReload(reg.waiting);

        reg.addEventListener('updatefound', () => {
          const next = reg.installing;
          if (!next) return;
          next.addEventListener('statechange', () => {
            // `controller` is null on the very first install, which is not an
            // update and must not prompt: there is nothing to replace and the
            // reader has seen this version for about a second.
            if (next.state === 'installed' && navigator.serviceWorker.controller) {
              offerReload(next);
            }
          });
        });
      } catch {
        // Registration refused. The map works; it is just not installable.
      }
    })();
  });

  // Fires once the replacement worker takes control, which only happens after
  // the reader has accepted. Guarded because a browser can fire it more than
  // once and a reload loop is a page nobody can leave.
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
}
